import React, { useState, useEffect, useRef, useContext } from 'react';
import { IFilledLocalAnalys } from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { ExportToPDFFnType } from '../../../../../../../Components/ExportToPDF/ExportToPDF';
import { IEkonomiskAnalysPDF } from '../../../../../../../Components/ExportToPDF/ExportToPDFArgumentTypes';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import EDOverlay from '../../../../../../../BaseComponents/Overlay/EDOverlay';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDDivider from '../../../../../../../BaseComponents/Divider/EDDivider';
import EkonomiskAnalysExportDetailsSettings, {
  firstVisibleColIndex,
} from '../EkonomiskAnalysExport/Components/EkonomiskAnalysExportDetailsSettings/EkonomiskAnalysExportDetailsSettings';
import EkonomiskAnalysExportSumSettings from '../EkonomiskAnalysExport/Components/EkonomiskAnalysExportSumSettings/EkonomiskAnalysExportSumSettings';
import EkonomiskAnalysExportLayoutSettings, {
  PrintMode,
} from '../EkonomiskAnalysExport/Components/EkonomiskAnalysExportLayoutSettings/EkonomiskAnalysExportLayoutSettings';
import EkonomiskAnalysExportToolbar from '../EkonomiskAnalysExportToolbar/EkonomiskAnalysExportToolbar';
import { ekonomiskAnalysDatesToDateString } from '../../../../../../../Utils/dateUtils';
import { IsPeriodLockedFnType } from '../../../../../../../Components/BokfaringsarActions/BokforingsarActions';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import SummeringsRecept from './Querys/EkonomiskAnalysSummeringsReceptQuery/EkonomiskAnalysSummeringsReceptQuery.gql';
import { useQuery } from '@apollo/client';
import {
  EkonomiskAnalysSummeringsReceptQuery,
  EkonomiskAnalysSummeringsReceptQueryVariables,
  EkonomiskAnalysSummeringsReceptQuery_db_EKDetaljeringsniva_All,
} from './Querys/EkonomiskAnalysSummeringsReceptQuery/_QueryTypes/EkonomiskAnalysSummeringsReceptQuery';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { getComparisonString } from '../../Utils/analysUtils';

interface IProps {
  open: boolean;
  onClose: () => void;
  analysSettings: IFilledLocalAnalys;
  selectedAnalys: ISelectedAnalys;
  exportToPDF: ExportToPDFFnType<IEkonomiskAnalysPDF>;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  radbaserad: boolean;
  isPeriodLocked: IsPeriodLockedFnType;
  classes?: any;
  forceHelarsjamforelse: boolean;
}

const INGET_SUMMERINGSRECEPT: EkonomiskAnalysSummeringsReceptQuery_db_EKDetaljeringsniva_All =
  {
    BENAMNING: 'Inget summeringsrecept',
    id: 'inget-summeringsrecept',
    __typename: 'TEDEKDetaljeringsniva',
  };

const styles = (theme: Theme) => ({
  savingOverlay: {
    right: 'calc(50% - 20px)',
    bottom: 'calc(50% - 20px)',
  },
});

/**
 * En modal för att bestämma hur en PDF utskrift för en ekonomisk analys skall se ut
 */
export const EkonomiskAnalysPrintPDFModal: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  const queryResult = useQuery<
    EkonomiskAnalysSummeringsReceptQuery,
    EkonomiskAnalysSummeringsReceptQueryVariables
  >(SummeringsRecept, { variables: { dbname: activeDatabase ?? '' } });

  const forceLandscapeMode =
    props.analysSettings.columns.filter(col => !col.data.HIDE_COLUMN).length >
    5;

  // Liggande/stående
  const [printMode, setPrintMode] = useState(
    forceLandscapeMode ? PrintMode.landscape : PrintMode.portrait
  );

  // Sumlevel
  const [sumLevel, setSumLevel] = useState(0);

  // Detaljerat kolumnval
  const [includeDetails, setIncludeDetails] = useState(false);
  const [detailColumnIndex, setDetailColumnIndex] = useState(
    firstVisibleColIndex(props.analysSettings.columns)
  );

  // Vissa nollbelopp
  const [showZeros, setShowZeros] = useState(false);

  // Markera negativa tal med rött
  const [markNegativeNumbers, setMarkNegativeNumbers] = useState(false);

  const [lopsaldoDetaljrad, setLopsaldoDetaljrad] = useState(
    props.analysSettings.lopsaldoDetaljrad
  );
  const [sortDateColumnAscending, setSortDateColumnAscending] = useState(
    props.analysSettings.lopsaldoDetaljrad
  );

  const [selectedSummeringsRecept, setSelectedSummeringsRecept] =
    useState<string>(INGET_SUMMERINGSRECEPT.id);

  // Håller på och laddar/sparar ner filen
  const [loading, setLoading] = useState(false);
  const prevLoading = useRef(loading);

  // detailColumnIndex får -1 vid första renderingen. Eftersom vi annars alltid vill komma ihåg staten så får vi köra detta
  useEffect(() => {
    if (props.open && detailColumnIndex === -1) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // Vill även glömma om vi byter analys
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.selectedAnalys,
    props.analysSettings.sums,
    props.analysSettings.columns,
  ]);

  // Vill inte kunna ligga kvar på portrait mode ifall vi vill tvinga fram landscape mode
  useEffect(() => {
    if (forceLandscapeMode && printMode === PrintMode.portrait) {
      setPrintMode(PrintMode.landscape);
    }
  }, [forceLandscapeMode, printMode]);

  useEffect(() => {
    if (loading === false && prevLoading.current === true) {
      props.onClose();
    }

    prevLoading.current = loading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const onSave = () => {
    props.exportToPDF(
      {
        reportName: 'EkonomiskAnalys',
        fileName: props.selectedAnalys.analys.name,
        analysid: props.selectedAnalys.analys.id,
        kodbeh_id: props.impersonatedCodePrivilege
          ? props.impersonatedCodePrivilege.id
          : props.selectedAnalys.codePrivilege.id,
        force_landscape: printMode === PrintMode.landscape,
        tidsintervall: {
          fromYear: props.analysSettings.dates.from.year,
          fromMonth: props.analysSettings.dates.from.month,
          fromDay: props.analysSettings.dates.from.day,
          toYear: props.analysSettings.dates.to.year,
          toMonth: props.analysSettings.dates.to.month,
          toDay: props.analysSettings.dates.to.day,
        },
        anvurval_selection: tryConvertUIUrvalToTEDUrval(
          props.analysSettings.urval
        ),
        grundurval_id: props.selectedAnalys.analys.grundurval
          ? props.selectedAnalys.analys.grundurval.id
          : undefined,
        detaljurval_selection: tryConvertUIUrvalToTEDUrval(
          props.analysSettings.detaljUrval
        ),
        force_helarsjamforelse: props.forceHelarsjamforelse,
        includeEjBokfort: Boolean(
          props.analysSettings.other.includeEjBokfordaJournaler
        ),
        analys:
          props.analysSettings.type === 'RAPPORTDIMENSIONLOPANDE' // TODO: Det här kanske ska ändras, om vi börjar använda annat än ID för radbaserade rapporter
            ? {
                force_helarsjamforelse: props.forceHelarsjamforelse,
                name: props.selectedAnalys.analys.name,
                columns: props.analysSettings.columns
                  .filter(col => !col.data.HIDE_COLUMN)
                  .map(col => col.data),
                sums: props.analysSettings.sums.slice(0, sumLevel + 1),
                rubrik: props.analysSettings.title ?? '',
                kategori: props.analysSettings.category || 'Standard',
              }
            : undefined,
        visadetaljer: includeDetails,
        detaljkolumn: detailColumnIndex,
        visanoll: showZeros,
        markeranegativasiffror: markNegativeNumbers,
        summerings_mode:
          selectedSummeringsRecept !== INGET_SUMMERINGSRECEPT.id
            ? selectedSummeringsRecept
            : props.analysSettings.other.summationMode.backendValue,
        budget_summerings_mode:
          props.analysSettings.other.budgetSummationMode.backendValue ??
          undefined,
        base: props.analysSettings.other.denomination,
        decimaler: props.analysSettings.other.decimals,
        jamforelsetal: getComparisonString(
          props.analysSettings.dates,
          props.analysSettings.isInterval,
          props.analysSettings.dateInputType,
          props.forceHelarsjamforelse
        ),
        fromPerString: ekonomiskAnalysDatesToDateString(
          props.analysSettings.dates,
          props.analysSettings.dateInputType,
          false
        ),
        tomPerString: props.analysSettings.isInterval
          ? ekonomiskAnalysDatesToDateString(
              props.analysSettings.dates,
              props.analysSettings.dateInputType,
              true
            )
          : null,
        periodLast: props.isPeriodLocked(
          props.analysSettings.dates.from.year,
          props.analysSettings.dates.from.month,
          props.analysSettings.dates.to.month,
          props.analysSettings.dateInputType
        ),
        lopsaldodetaljrad: lopsaldoDetaljrad,
        datesortascending: sortDateColumnAscending,
        usesummeringsrecept:
          selectedSummeringsRecept !== INGET_SUMMERINGSRECEPT.id,
      },
      setLoading
    );
  };

  const reset = () => {
    setPrintMode(forceLandscapeMode ? PrintMode.landscape : PrintMode.portrait);
    setSumLevel(0);
    setIncludeDetails(false);
    setDetailColumnIndex(firstVisibleColIndex(props.analysSettings.columns));
    setShowZeros(false);
    setMarkNegativeNumbers(false);
    setLopsaldoDetaljrad(props.analysSettings.lopsaldoDetaljrad);
    setSortDateColumnAscending(props.analysSettings.lopsaldoDetaljrad);
  };

  const footerActions = () => {
    const avbryt = { label: 'Avbryt', onClick: props.onClose };
    const skrivUt = {
      label: loading ? 'Skriver ut' : 'Skriv ut',
      onClick: onSave,
      buttonProps: {
        disabled: loading,
      },
    };

    if (loading) {
      return [skrivUt];
    }

    return [avbryt, skrivUt];
  };

  return (
    <Dialog
      title="Skriv ut analys"
      open={props.open}
      onClose={props.onClose}
      footerActions={footerActions()}
      headerTools={<EkonomiskAnalysExportToolbar reset={reset} />}
    >
      <EDOverlay
        overlay={loading && <EDAvenyProgress />}
        classes={{ overlay: props.classes.savingOverlay }}
      >
        <div>
          <EDTooltip
            title={
              forceLandscapeMode
                ? 'Kan ej skriva ut analys med fler än 5 kolumner i stående format'
                : loading
                ? 'Skriver ut analys'
                : undefined
            }
          >
            <div>
              <EkonomiskAnalysExportLayoutSettings
                printMode={printMode}
                setPrintMode={setPrintMode}
                portraitDisabled={forceLandscapeMode || loading}
                landscapeDisabled={loading}
              />
            </div>
          </EDTooltip>
          <EDDivider />
          {!props.radbaserad && (
            <EkonomiskAnalysExportSumSettings
              analysSettings={props.analysSettings}
              sumLevel={sumLevel}
              setSumLevel={setSumLevel}
              marginTop={2}
            />
          )}
          <EDDivider />
          <EkonomiskAnalysExportDetailsSettings
            analysSettings={props.analysSettings}
            selectedAnalys={props.selectedAnalys}
            includeDetails={includeDetails}
            setIncludeDetails={(value: boolean) => setIncludeDetails(value)}
            detailColumnIndex={detailColumnIndex}
            setDetailColumnIndex={setDetailColumnIndex}
            radbaserad={props.radbaserad}
            lopsaldoDetaljrad={lopsaldoDetaljrad}
            setLopsaldoDetaljrad={setLopsaldoDetaljrad}
            sortDateColumnAscending={sortDateColumnAscending}
            setSortDateColumnAscending={setSortDateColumnAscending}
            summeringsRecept={
              [INGET_SUMMERINGSRECEPT].concat(
                queryResult.data?.db?.EKDetaljeringsniva_All ?? []
              ) ?? undefined
            }
            selectedSummeringsRecept={selectedSummeringsRecept}
            setSelectedSummeringsRecept={setSelectedSummeringsRecept}
          />
          <EDDivider />
          <EDBox marginTop={2}>
            <EDCheckbox
              label="Visa nollbelopp"
              checked={showZeros}
              onCheck={(value: boolean) => setShowZeros(value)}
              checkboxSpacing={{ paddingY: 0.5 }}
            />
            <EDCheckbox
              label="Markera negativa siffror i rött"
              checked={markNegativeNumbers}
              onCheck={(value: boolean) => setMarkNegativeNumbers(value)}
              checkboxSpacing={{ paddingY: 0.5 }}
            />
          </EDBox>
        </div>
      </EDOverlay>
    </Dialog>
  );
};

export default withStyles(EkonomiskAnalysPrintPDFModal, styles);
